import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	InputAdornment,
} from '@mui/material';

import { useAllSubTypesByType } from '../../pages/Project/hooks/types';
import { fetchAllSubValuesBySubType } from '../../services/type.service';
import { addMetalGateSubtype } from '../../services/project.service';
import {
	ProjectToTypesFullDetailsDataModel,
	SubTypeDataModel as ProjectSubTypeDataModel,
} from '../../models/project.model';
import { SubTypeDataModel, SubValueDataModel } from '../../models/types.model';

export const AddMetalGateSubtypeDialog = (props: any) => {
	const [subTypeId, setSubTypeId] = useState<number | ''>('');
	const [subTypeValueId, setSubTypeValueId] = useState<number | ''>('');
	const [subTypeValues, setSubTypeValues] = useState<Array<SubValueDataModel>>([]);
	const [purchaseCost, setPurchaseCost] = useState<string>('');
	const [sellingPrice, setSellingPrice] = useState<string>('');
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [loadingValues, setLoadingValues] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const subTypeValuesRequestId = useRef(0);

	const selectedProjectType: ProjectToTypesFullDetailsDataModel | null | undefined = props.selectedProjectType;
	const subTypesList: Array<SubTypeDataModel> | undefined = useAllSubTypesByType(selectedProjectType?.typeId);

	const existingValueIds = useMemo(() => {
		const match = selectedProjectType?.subTypes?.find((st: ProjectSubTypeDataModel) => st.subTypeId === subTypeId);
		return new Set((match?.subtypeValuesList || []).map((v) => v.id));
	}, [selectedProjectType, subTypeId]);

	const availableSubTypeValues = subTypeValues.filter((value: SubValueDataModel) => !existingValueIds.has(value.id));

	useEffect(() => {
		setSubTypeValueId('');
		setSubTypeValues([]);

		if (!subTypeId) return;

		const requestId = ++subTypeValuesRequestId.current;
		setLoadingValues(true);
		fetchAllSubValuesBySubType('type/subtype/value/getall?subtypeId=' + subTypeId)
			.then((response: any) => {
				if (requestId !== subTypeValuesRequestId.current) return;
				if (!response.data?.success) {
					toast.error(response.data?.message || 'Unable to load subtype values.');
					return;
				}
				setSubTypeValues(response.data.data);
			})
			.catch((err: any) => {
				if (requestId === subTypeValuesRequestId.current) {
					toast.error(err?.response?.data?.message || err?.message || 'Unable to load subtype values.');
				}
			})
			.finally(() => {
				if (requestId === subTypeValuesRequestId.current) setLoadingValues(false);
			});
	}, [subTypeId]);

	const resetFields = () => {
		subTypeValuesRequestId.current += 1;
		setSubTypeId('');
		setSubTypeValueId('');
		setSubTypeValues([]);
		setPurchaseCost('');
		setSellingPrice('');
		setLoadingValues(false);
		setImageFile(null);
		setFileError('');
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const onClose = () => {
		resetFields();
		setLoading(false);
		props.close();
	};

	const onFileChange = (event: any) => {
		const files: FileList | null = event.target.files;
		if (!files || files.length !== 1) {
			setImageFile(null);
			setFileError('Please select exactly one image file.');
			return;
		}
		const file = files[0];
		if (!file.type.startsWith('image/')) {
			setImageFile(null);
			setFileError('Selected file must be an image.');
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}
		setFileError('');
		setImageFile(file);
	};

	const handleSubmit = async () => {
		if (
			!selectedProjectType?.id ||
			!subTypeId ||
			!subTypeValueId ||
			!imageFile ||
			purchaseCost === '' ||
			sellingPrice === ''
		) {
			toast.error('Please select a sub type, a value, a sub cost, a contract price and an image before submitting.');
			return;
		}

		try {
			setLoading(true);
			const result: any = await addMetalGateSubtype(
				selectedProjectType.id,
				subTypeId,
				subTypeValueId,
				Number(purchaseCost),
				Number(sellingPrice),
				imageFile
			);

			if (result.data.success) {
				toast.success(result.data.message);
				resetFields();
				props.onRefreshTypePanel();
			} else {
				toast.error(result.data.message);
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={props.show} onClose={onClose} aria-labelledby='customized-dialog-title'>
			<DialogTitle>Add Metal Gate</DialogTitle>
			<DialogContent style={{ width: '400px' }}>
				<FormControl fullWidth sx={{ marginTop: '20px' }}>
					<InputLabel>{'Sub Type'}</InputLabel>
					<Select
						label='Sub Type'
						variant='outlined'
						value={subTypeId}
						onChange={(e: any) => {
							setSubTypeId(e.target.value);
						}}>
						{subTypesList?.length &&
							subTypesList.map((subType: SubTypeDataModel, index: number) => (
								<MenuItem key={index} value={subType.id}>
									{subType.name}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl fullWidth sx={{ marginTop: '20px' }} disabled={!subTypeId || loadingValues}>
					<InputLabel>{'Value'}</InputLabel>
					<Select
						label='Value'
						variant='outlined'
						value={subTypeValueId}
						onChange={(e: any) => {
							setSubTypeValueId(e.target.value);
						}}>
						{availableSubTypeValues.map((value: SubValueDataModel, index: number) => (
							<MenuItem key={index} value={value.id}>
								{value.value}
							</MenuItem>
						))}
					</Select>
					{subTypeId && !loadingValues && subTypeValues.length > 0 && availableSubTypeValues.length === 0 ? (
						<div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
							All values for this subtype have already been added.
						</div>
					) : null}
				</FormControl>

				<TextField
					style={{ marginTop: '20px' }}
					label='Sub Cost'
					type='number'
					InputProps={{
						startAdornment: <InputAdornment position='start'>$</InputAdornment>,
						inputProps: { min: 0 },
					}}
					value={purchaseCost}
					onChange={(e: any) => {
						setPurchaseCost(e.target.value);
					}}
					variant='outlined'
					fullWidth
				/>

				<TextField
					style={{ marginTop: '20px' }}
					label='Contract Price'
					type='number'
					InputProps={{
						startAdornment: <InputAdornment position='start'>$</InputAdornment>,
						inputProps: { min: 0 },
					}}
					value={sellingPrice}
					onChange={(e: any) => {
						setSellingPrice(e.target.value);
					}}
					variant='outlined'
					fullWidth
				/>

				<div style={{ marginTop: '20px' }}>
					<input ref={fileInputRef} type='file' accept='image/*' onChange={onFileChange} />
					{fileError ? <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{fileError}</div> : null}
				</div>
			</DialogContent>
			<DialogActions>
				{loading ? (
					<CircularProgress color='primary' />
				) : (
					<>
						<Button variant='outlined' size='small' style={{ marginLeft: '20px' }} onClick={onClose}>
							Cancel
						</Button>
						<Button
							type='submit'
							size='small'
							variant='contained'
							disabled={!subTypeId || !subTypeValueId || purchaseCost === '' || sellingPrice === '' || !imageFile}
							onClick={handleSubmit}>
							Add
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};
